import { BrowserRouter, Routes, Route, Link } from 'react-router-dom';
import { useState } from 'react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface LoginModalProps extends ModalProps {
  onShowRegister: () => void;
}

interface RegisterModalProps extends ModalProps {
  onShowLogin: () => void;
}

// Components
function LoginModal({ isOpen, onClose, onShowRegister }: LoginModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">התחברות</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <form className="space-y-4" onSubmit={e => e.preventDefault()}>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="email">
              אימייל
            </label>
            <input
              type="email"
              id="email"
              className="w-full p-2 border rounded-md"
              required
            />
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="password">
              סיסמה
            </label>
            <input
              type="password"
              id="password"
              className="w-full p-2 border rounded-md"
              required
            />
          </div>
          <button
            type="submit"
            className="w-full bg-green-500 text-white py-2 px-4 rounded-md hover:bg-green-600 transition"
          >
            התחבר
          </button>
          <p className="text-center text-sm">
            אין לך חשבון?{' '}
            <button
              onClick={onShowRegister}
              className="text-green-500 hover:text-green-600 font-medium"
            >
              הירשם עכשיו
            </button>
          </p>
        </form>
      </div>
    </div>
  );
}

function RegisterModal({ isOpen, onClose, onShowLogin }: RegisterModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">הרשמה</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <form className="space-y-4" onSubmit={e => e.preventDefault()}>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">
              שם מלא
            </label>
            <input type="text" className="w-full p-2 border rounded-md" required />
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">
              אימייל
            </label>
            <input type="email" className="w-full p-2 border rounded-md" required />
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">
              סיסמה
            </label>
            <input type="password" className="w-full p-2 border rounded-md" required />
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">
              טלפון
            </label>
            <input type="tel" className="w-full p-2 border rounded-md" required />
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">
              קבוצת גיל
            </label>
            <select className="w-full p-2 border rounded-md" required>
              <option value="">בחר קבוצת גיל</option>
              <option value="6-8">גילאי 6-8</option>
              <option value="9-12">גילאי 9-12</option>
              <option value="13-15">גילאי 13-15</option>
            </select>
          </div>
          <button
            type="submit"
            className="w-full bg-green-500 text-white py-2 px-4 rounded-md hover:bg-green-600 transition"
          >
            הירשם
          </button>
          <p className="text-center text-sm">
            יש לך כבר חשבון?{' '}
            <button
              onClick={onShowLogin}
              className="text-green-500 hover:text-green-600 font-medium"
            >
              התחבר
            </button>
          </p>
        </form>
      </div>
    </div>
  );
}

function Router() {
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);

  return (
    <BrowserRouter>
      <Routes>
        <Route path='/' element={
          <div className="min-h-screen text-right font-arimo" dir="rtl">
            {/* Header */}
            <header className="relative text-white">
              {/* Background Image */}
              <div 
                className="absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat" 
                style={{
                  backgroundImage: 'url("/football.webp")',
                  backgroundPosition: 'center',
                  filter: 'brightness(0.6)'
                }}
              />
              
              {/* Navigation - with relative positioning to appear above the background */}
              <nav className="relative container mx-auto px-4 py-4 flex justify-between items-center bg-black/20">
                <div className="flex items-center space-x-4 space-x-reverse">
                  <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" />
                  </svg>
                  <span className="text-xl font-bold">מועדון הכדורגל</span>
                </div>
                <div className="flex items-center">
                  <div className="flex items-center gap-12">
                    <a href="#about" className="hover:text-green-200 px-2">אודות</a>
                    <a href="#programs" className="hover:text-green-200 px-2">תכניות אימון</a>
                    <a href="#contact" className="hover:text-green-200 px-2">צור קשר</a>
                  </div>
                  <div className="mx-12 border-r border-white/30 h-5"></div>
                  <button
                    onClick={() => setShowLogin(true)}
                    className="hover:text-green-200 transition-colors duration-200 px-2"
                  >
                    התחבר
                  </button>
                </div>
              </nav>

              {/* Hero Section */}
              <div className="relative container mx-auto px-4 py-32 text-center">
                <div className="max-w-3xl mx-auto">
                  <h1 className="text-5xl font-bold mb-6 text-shadow">גלה את הכדורגלן שבך!</h1>
                  <p className="text-xl mb-8 text-shadow-sm">הצטרף למועדון הכדורגל המוביל לצעירים</p>
                  <button 
                    onClick={() => setShowRegister(true)}
                    className="bg-white text-green-600 px-8 py-3 rounded-md text-lg font-semibold hover:bg-green-100 transition-all transform hover:scale-105 hover:shadow-lg"
                  >
                    התחל להתאמן עכשיו
                  </button>
                </div>
              </div>
            </header>

            {/* Features Section */}
            <section id="about" className="py-16 bg-gray-50">
              <div className="container mx-auto px-4">
                <h2 className="text-3xl font-bold text-center mb-12">למה לבחור בנו?</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <div className="bg-white p-6 rounded-lg shadow-md">
                    <div className="text-green-600 mb-4">
                      <svg className="w-12 h-12 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-center mb-2">מאמנים מוסמכים</h3>
                    <p className="text-gray-600 text-center">צוות מאמנים מקצועי עם ניסיון עשיר בעבודה עם שחקנים צעירים</p>
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow-md">
                    <div className="text-green-600 mb-4">
                      <svg className="w-12 h-12 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-center mb-2">מתקנים חדישים</h3>
                    <p className="text-gray-600 text-center">מגרשי דשא סינטטי חדשים וציוד מתקדם לאימונים</p>
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow-md">
                    <div className="text-green-600 mb-4">
                      <svg className="w-12 h-12 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-center mb-2">פיתוח מקצועי</h3>
                    <p className="text-gray-600 text-center">תכנית אימונים מותאמת אישית לכל שחקן</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Programs Section */}
            <section id="programs" className="py-16">
              <div className="container mx-auto px-4">
                <h2 className="text-3xl font-bold text-center mb-12">תכניות האימון שלנו</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {[
                    {
                      title: 'מתחילים',
                      age: 'גילאי 6-8',
                      features: ['יסודות הכדורגל', 'משחקים קבוצתיים', 'פיתוח מיומנויות בסיסיות']
                    },
                    {
                      title: 'מתקדמים',
                      age: 'גילאי 9-12',
                      features: ['טקטיקה מתקדמת', 'אימוני כושר', 'טכניקות מתקדמות']
                    },
                    {
                      title: 'נבחרת צעירה',
                      age: 'גילאי 13-15',
                      features: ['אימונים אינטנסיביים', 'השתתפות בתחרויות', 'הכנה למשחקים מקצועיים']
                    }
                  ].map((program, index) => (
                    <div key={index} className="bg-white p-6 rounded-lg shadow-md">
                      <h3 className="text-2xl font-bold text-center text-green-600 mb-2">{program.title}</h3>
                      <p className="text-lg text-center text-gray-600 mb-4">{program.age}</p>
                      <ul className="space-y-2">
                        {program.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center">
                            <svg className="w-5 h-5 text-green-500 ml-2" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Contact Section */}
            <section id="contact" className="py-16 bg-gray-800 text-white">
              <div className="container mx-auto px-4">
                <h2 className="text-3xl font-bold text-center mb-12">צור קשר</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
                  <div className="text-center">
                    <svg className="w-8 h-8 mx-auto mb-4" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                    </svg>
                    <p>054-1234567</p>
                  </div>
                  <div className="text-center">
                    <svg className="w-8 h-8 mx-auto mb-4" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                      <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                    </svg>
                    <p>info@football-club.co.il</p>
                  </div>
                  <div className="text-center">
                    <svg className="w-8 h-8 mx-auto mb-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                    <p>רחוב הספורט 1, תל אביב</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Modals */}
            <LoginModal
              isOpen={showLogin}
              onClose={() => setShowLogin(false)}
              onShowRegister={() => {
                setShowLogin(false);
                setShowRegister(true);
              }}
            />
            <RegisterModal
              isOpen={showRegister}
              onClose={() => setShowRegister(false)}
              onShowLogin={() => {
                setShowRegister(false);
                setShowLogin(true);
              }}
            />
          </div>
        }
        />
        {/* 404 Page: */}
        <Route
          path='*'
          element={
            <div className="flex flex-col items-center justify-center min-h-[70vh]">
              <h1 className="text-3xl font-bold mb-4">Page Not Found</h1>
              <p className="mb-4">The page you're looking for doesn't exist.</p>
              <Link to="/" className="text-indigo-600 hover:text-indigo-800">
                Go Home
              </Link>
            </div>
          }
        />
      </Routes>
    </BrowserRouter>
  )
}

export default Router
